import { DOCUMENT } from '@angular/common';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { APP_CONFIG } from '@environments/environment';
import { finalize, fromEvent, Subscription } from 'rxjs';
import * as AuthActions from '@/app/+state/auth/auth.actions';
import { TokenStorageService } from './token-storage.service';
import { SESSION_SETTLE_MS } from './user-activity.service';

/** Cross-window password revocation; the server remains the authority. */
@Injectable({ providedIn: 'root' })
export class PasswordSessionService implements OnDestroy {
    private readonly document = inject(DOCUMENT);
    private readonly tokens = inject(TokenStorageService);
    private readonly store = inject(Store);
    private readonly zone = inject(NgZone);
    // A status probe must never trigger the token interceptor's renewal path.
    private readonly http = new HttpClient(inject(HttpBackend));
    private readonly subscriptions = new Subscription();
    private readonly eventKey = 'qc:password-changed';
    private timer?: ReturnType<typeof setInterval>;
    private settleTimer?: ReturnType<typeof setTimeout>;
    private checking = false;
    private observedIdentity: { sub: string; passwordVersion?: number } | null = null;

    identity(token: string | null): { sub: string; passwordVersion?: number } | null {
        try {
            const payload = JSON.parse(atob(token!.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            return typeof payload.sub === 'string' ? payload : null;
        } catch { return null; }
    }

    start(): void {
        const window = this.document.defaultView;
        if (!window || this.timer !== undefined) return;
        this.subscriptions.add(this.tokens.tokens$.subscribe(({ accessToken }) => {
            this.observedIdentity = this.identity(accessToken);
        }));
        this.zone.runOutsideAngular(() => {
            this.subscriptions.add(fromEvent<StorageEvent>(window, 'storage').subscribe(event => {
                if (event.key !== this.eventKey || !event.newValue) return;
                try {
                    const message = JSON.parse(event.newValue);
                    // Remembered tokens may already have been removed by the sender.
                    const current = this.identity(this.tokens.getAccessToken()) ?? this.observedIdentity;
                    if (!current || current.sub !== message.userId) return;
                    if (typeof message.previousVersion === 'number' && typeof current.passwordVersion === 'number'
                        && current.passwordVersion > message.previousVersion) return;
                    this.endSession();
                } catch { /* Ignore unrelated/malformed storage messages. */ }
            }));
            this.subscriptions.add(fromEvent(window, 'focus').subscribe(() => this.checkSession()));
            this.subscriptions.add(fromEvent(this.document, 'visibilitychange').subscribe(() => this.checkSession()));
            this.timer = setInterval(() => this.checkSession(), 30_000);
            this.checkSession();
        });
    }

    passwordChanged(identity: { sub: string; passwordVersion?: number } | null, endCurrentSession = true): void {
        if (!identity) return;
        try {
            // Contains no passwords or tokens. A nonce makes repeated changes observable.
            this.document.defaultView?.localStorage.setItem(this.eventKey, JSON.stringify({
                userId: identity.sub, previousVersion: identity.passwordVersion, nonce: crypto.randomUUID(),
            }));
        } catch { /* Server checks still enforce revocation if storage is unavailable. */ }
        if (endCurrentSession) this.endSession();
        else if (this.identity(this.tokens.getAccessToken())?.sub === identity.sub) {
            // A reset tab may still have its old login tokens. Drop them without
            // firing logoutSuccess, whose effect would erase the ongoing challenge.
            this.tokens.clearTokens();
        }
    }

    private checkSession(): void {
        if (this.document.visibilityState === 'hidden' || this.checking) return;
        const refreshToken = this.tokens.getRefreshToken();
        if (!refreshToken) return;
        this.checking = true;
        this.subscriptions.add(this.http.post(`${APP_CONFIG.apiUrl}/auth/session`, { refreshToken }, { withCredentials: true })
            .pipe(finalize(() => { this.checking = false; }))
            .subscribe({
                error: error => {
                    // A renewal racing this check rotates the token it carried; only end the
                    // session if the token is still current once concurrent renewals settle.
                    if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
                        clearTimeout(this.settleTimer);
                        this.settleTimer = setTimeout(() => {
                            if (this.tokens.getRefreshToken() === refreshToken) this.endSession();
                        }, SESSION_SETTLE_MS);
                    }
                    // Network/server failures do not establish that credentials were revoked.
                },
            }));
    }

    private endSession(): void {
        this.zone.run(() => {
            this.tokens.clearTokens();
            this.store.dispatch(AuthActions.logoutSuccess());
        });
    }

    ngOnDestroy(): void {
        clearInterval(this.timer);
        clearTimeout(this.settleTimer);
        this.subscriptions.unsubscribe();
    }
}
